<script setup lang="ts">

import { onMounted, watch } from 'vue'
import { useLeaderBoardStore } from '@/stores/leaderBoardStore.ts'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import PlanetIcon from '@/components/Objects/Icons/PlanetIcon.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import PlayerName from '@/components/Objects/PlayerName.vue'
import { firstCamel } from '@/models/tools.ts'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import { useRouter } from 'vue-router'
import { CDN } from '@/models/cdn.ts'

/**
 * Stores / models
 */
const leaderBoardStore = useLeaderBoardStore()
const playerRelationStore = usePlayerRelationStore()
const gameStore = useGameStore()
const assetStore = useAssetStore()
const mapCoordinateStore = useMapCoordinateStore()
const allianceStore = useAllianceStore()
const router = useRouter()

/**
 * Functions
 */
const filters = (players:Map<number,any>) => {
  return Array.from(players.values())
}

const loadPlayer = (playerId:number) => {
  if(playerId === gameStore.player?.id) {
    return
  }
  gameStore.modalType = 'requestAudience'
  gameStore.modalData = {
    relation: playerRelationStore.findRelationByPlayerId(playerId)
  }
}

const getAllianceName = (playerId: number) => {
  // If this is the current player
  if (playerId === gameStore.player?.id && gameStore.player?.allianceId) {
    const alliance = allianceStore.getAlliance(gameStore.player.allianceId)
    return alliance?.name || ''
  }
  
  // For other players, check all alliances for membership
  for (const alliance of allianceStore.alliances.values()) {
    if (alliance.members && alliance.members.includes(playerId)) {
      return alliance.name
    }
  }
  return ''
}

const getAllianceId = (playerId: number) => {
  // If this is the current player
  if (playerId === gameStore.player?.id && gameStore.player?.allianceId) {
    return gameStore.player.allianceId
  }
  
  // For other players, check all alliances for membership
  for (const alliance of allianceStore.alliances.values()) {
    if (alliance.members && alliance.members.includes(playerId)) {
      return alliance.id
    }
  }
  return 0
}

const viewAllianceDetails = (playerId: number) => {
  const allianceId = getAllianceId(playerId)
  if (!allianceId) return // No alliance to view
  
  // Check if player is a member of this alliance
  if (gameStore.player?.allianceId === allianceId) {
    // Use router to navigate to the alliance page
    router.push('/diplomacy/alliance')
  } else {
    // Open modal for viewing other alliances
    gameStore.modalType = 'viewAlliance'
    gameStore.modalData = { allianceId }
  }
}

onMounted(async () => {
  // Start building the leaderboard from map data
  await leaderBoardStore.generateLeaderBoard();
});

watch(
  [() => mapCoordinateStore.planets, () => mapCoordinateStore.fleets, () => leaderBoardStore.dayAllyDataRetrieved],
  async () => {
    await leaderBoardStore.generateLeaderBoard();
  },
  { deep: true }
);


</script>

<template>
<div class="pb-2 pt-2 w-full xl:w-2/3 mx-auto px-2 md:px-0">
    <FerionDataTable
      :data="leaderBoardStore.players"
      tableStoreName="leaderboard"
      :selector="false"
      :filters="filters"
      :heightLimit="'h-[calc(100vh-15rem)]'"
      :no-result-message="'No data yet...'"
      :click="loadPlayer"
      :footer-bar="false"
    >
      <!--- Headers --->
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderFixedWidthText width="3" :sort-direction="sortDirection" :sort-column="sortColumn" column="playerId" class="rounded-tl"/>
        <HeaderTextFlex min-width="10" column="name" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <div class="pl-2">Name</div>
        </HeaderTextFlex>
        <HeaderFixedWidthText width="3" :sort-direction="sortDirection" :sort-column="sortColumn" column="country" @sort="sort">

        </HeaderFixedWidthText>
        <HeaderFixedWidthText width="8" :sort-direction="sortDirection" :sort-column="sortColumn" column="relation" @sort="sort">
          <div class="text-center">
            Status
          </div>
        </HeaderFixedWidthText>
        <HeaderFixedWidthText width="10" :sort-direction="sortDirection" :sort-column="sortColumn" column="alliance" @sort="sort">
          <div class="text-center">
            Alliance
          </div>
        </HeaderFixedWidthText>
        <HeaderFixedWidthText width="2" :sort-direction="sortDirection" :sort-column="sortColumn" column="otherRequested" @sort="sort">
          <div class="text-center">
            <span
              class="rounded bg-slate-500 text-white text-xs px-1"
            >!</span>
          </div>
        </HeaderFixedWidthText>
        <HeaderFixedWidthIcon width="4" column="planets" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <div class="flex justify-center">
            <span class=""><PlanetIcon :size="2" :type="5"></PlanetIcon></span>
          </div>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="4" column="ships" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <div class="flex justify-center">
            <GenericIcon tooltip="Ship count" type="ship" class="rounded mt-1"/>
          </div>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthTextNoSort width="20"  class="rounded-tr">
          <div class="flex justify-center">Researching</div>
        </HeaderFixedWidthTextNoSort>
      </template>
      <!--- Rows --->
      <template #rows="{ item }" >
        <CellFixedWidth width="3">
          <div class="flex-col">
            <img :src="'https://wsrv.nl/?url=' + item.profileImage + '&w=36&h=36'" class="w-9 h-9 rounded" alt="Profile image">
          </div>
        </CellFixedWidth>
        <CellFlexWidth min-width="10">
          <div class="flex-col ml-1">
            <PlayerName :player-id="item.playerId"/>
          </div>
        </CellFlexWidth>
        <CellFixedWidth width="3">
          <div class="flex-col">
            <img alt="NA" :src="CDN.countryFlag(item.country, '32x24')" class="h-3 rounded">
          </div>
        </CellFixedWidth>
        <CellFixedWidth width="8" align="center">
          <span class="text-subtext" v-if="item.relation !='self'">{{ firstCamel(item.relation) }}</span>
        </CellFixedWidth>
        <CellFixedWidth width="10" align="center">
          <span 
            v-if="getAllianceId(item.playerId)" 
            class="cursor-pointer"
            @click.stop="viewAllianceDetails(item.playerId)"
          >
            {{ getAllianceName(item.playerId) }}
          </span>
        </CellFixedWidth>
        <CellFixedWidth width="2">
            <span
              v-if="item.otherRequested !== 'none'"
              class="rounded bg-red-500 text-slate-300 text-xs px-1"
            >!</span>
        </CellFixedWidth>
        <CellFixedWidth width="4">
          <span v-if="item.exact">{{ item.planets }}</span>
          <span v-else class="text-subtext">{{ item.planets }}</span>
        </CellFixedWidth>
        <CellFixedWidth width="4">
          <span v-if="item.exact">{{ item.ships }}</span>
          <span v-else class="text-subtext">{{ item.ships }}</span>
        </CellFixedWidth>
        <CellFixedWidth width="20">
          <span v-if="item.currentTechId>0">{{ assetStore.findTechById(item.currentTechId)?.name }}</span>
        </CellFixedWidth>
      </template>

    </FerionDataTable>
</div>
</template>

<style scoped lang="scss">

</style>